import type {
  AvailableSourceTableDto,
  BcApiEndpoint,
} from '@/api/control/missionImpactAPI.schemas';
import { exceedsIdentifierLimit } from '@/lib/tablePrefix';

/**
 * Per-table configuration tracked for each selected table. Mirrors the
 * BC-specific fields needed by AddBusinessCentralSourceTableRequest; for
 * Dataverse and Salesforce tables these are simply left undefined.
 */
export interface SelectedTableConfig {
  apiEndpoint?: BcApiEndpoint;
  timestampField?: string;
  hasIsDeletedField?: boolean;
  isDeletedFieldName?: string;
  /** Friendly display name from the discovery data, persisted at selection time. */
  displayName?: string;
}

/**
 * Whether a table can be selected: the server must not have disabled it, and its prefixed staging
 * table name (<c>stage_{prefix}{logicalName}</c>) must not exceed the 128-char SQL identifier limit
 * under the target's table prefix.
 */
export function isTableEligible(table: AvailableSourceTableDto, tablePrefix: string): boolean {
  return table.isEnabled && !exceedsIdentifierLimit(tablePrefix, table.logicalName);
}

/**
 * Builds the per-table config to store when a table is selected. Business Central tables carry
 * endpoint-specific defaults (V2 auto-resolves its default timestamp field; ODataV4 starts in
 * full-replication mode until the user picks one); Dataverse and Salesforce tables only carry the
 * display name.
 */
export function buildTableConfig(table: AvailableSourceTableDto): SelectedTableConfig {
  if (table.$type === 'BusinessCentral') {
    const autoTimestamp = table.apiEndpoint === 'V2'
      ? (table.defaultTimestampField ?? undefined)
      : undefined;
    return {
      apiEndpoint: table.apiEndpoint,
      timestampField: autoTimestamp,
      hasIsDeletedField: table.hasIsDeletedField ?? undefined,
      isDeletedFieldName: table.isDeletedFieldName ?? undefined,
      displayName: table.displayName || undefined,
    };
  }

  return { displayName: table.displayName || undefined };
}

/**
 * The state of a "select all" header control relative to the currently visible (filtered) tables.
 */
export interface SelectAllState {
  /** Number of eligible (selectable) tables in the filtered list. */
  eligibleCount: number;
  /** Number of eligible filtered tables currently selected. */
  selectedEligibleCount: number;
  /** True when every eligible filtered table is selected (and there is at least one). */
  allSelected: boolean;
  /** True when some — but not all — eligible filtered tables are selected (indeterminate). */
  someSelected: boolean;
}

/**
 * Computes the select-all header state over the currently filtered tables, skipping ineligible
 * rows (server-disabled, or whose prefixed name exceeds the identifier limit).
 */
export function computeSelectAllState(
  filteredTables: AvailableSourceTableDto[],
  tablePrefix: string,
  value: ReadonlyMap<string, SelectedTableConfig>,
): SelectAllState {
  const eligible = filteredTables.filter((table) => isTableEligible(table, tablePrefix));
  const selectedEligibleCount = eligible.filter((table) => value.has(table.logicalName)).length;
  const eligibleCount = eligible.length;

  return {
    eligibleCount,
    selectedEligibleCount,
    allSelected: eligibleCount > 0 && selectedEligibleCount === eligibleCount,
    someSelected: selectedEligibleCount > 0 && selectedEligibleCount < eligibleCount,
  };
}

/**
 * Applies a select-all / deselect-all toggle over the currently filtered tables, returning the next
 * selection map. Ineligible rows are never added. When every eligible filtered table is already
 * selected, the toggle deselects those filtered tables; otherwise it selects all eligible filtered
 * tables. Selections outside the current filter are preserved.
 */
export function applySelectAll(
  filteredTables: AvailableSourceTableDto[],
  tablePrefix: string,
  value: ReadonlyMap<string, SelectedTableConfig>,
): Map<string, SelectedTableConfig> {
  const next = new Map(value);
  const eligible = filteredTables.filter((table) => isTableEligible(table, tablePrefix));
  const allSelected =
    eligible.length > 0 && eligible.every((table) => next.has(table.logicalName));

  if (allSelected) {
    // Deselect only the tables currently in view; selections outside the filter are untouched.
    for (const table of eligible) {
      next.delete(table.logicalName);
    }
  } else {
    for (const table of eligible) {
      if (!next.has(table.logicalName)) {
        next.set(table.logicalName, buildTableConfig(table));
      }
    }
  }

  return next;
}
